import { useDataFetcher } from 'hooks/useDataFetcher';
import { useMutation } from 'hooks/useMutation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchInventory, fetchInventoryColumnsByLocation } from 'services/inventoryService';
import { fetchYardCheckSummary as fetchSummary, submitYardCheckSummary } from 'services/yardCheckService';
import { SUMMARY_ACTION, YARD_CHECK_ITEM_TYPE } from 'utils/constants';
import { groupFieldsByHeader } from 'utils/formFieldHelper';
import { deepSearch } from 'utils/utils';
import { mapYardCheckInfo, mapYardCheckSummary } from '../mapper';

const { MATCHED, MISSING, NEW, UPDATED } = YARD_CHECK_ITEM_TYPE;

const useYardCheckSummary = ({ summaryId, selectedLocationId }) => {
    const summaryIdRef = useRef(null);
    const [summaryTableData, setSummaryTableData] = useState([]);
    const [payload, setPayload] = useState({ yardCheckItems: [], locationId: selectedLocationId });
    const [displayedTableData, setDisplayedTableData] = useState([]);
    const [filterCriteria, setFilterCriteria] = useState({
        searchText: '',
        statuses: {
            [MISSING]: true,
            [NEW]: true,
            [UPDATED]: true,
            [MATCHED]: true,
        },
    });
    const { data = [], fetchData, isLoading, isFetching } = useDataFetcher([fetchSummary, fetchInventoryColumnsByLocation, fetchInventory]);
    const { mutate: onSubmitYardCheckSummary, isLoading: isSubmitYardCheckLoading } = useMutation({ mutationFn: submitYardCheckSummary });

    const fetchYardCheckSummary = useCallback(() => {
        if (summaryIdRef.current !== summaryId) {
            fetchData([summaryId], [{ locationId: selectedLocationId }], [{ locationId: selectedLocationId }]);
            summaryIdRef.current = summaryId;
        }
    }, [fetchData, selectedLocationId, summaryId]);

    const handleRefresh = useCallback(() => {
        if (summaryId) {
            fetchData([summaryId], [{ locationId: selectedLocationId }], [{ locationId: selectedLocationId }]);
        }
    }, [summaryId, fetchData, selectedLocationId]);

    const {
        tableData,
        tableColumns,
        yardCheckInfo = {},
    } = useMemo(() => {
        return {
            tableData: mapYardCheckSummary(data[0] || [], data[2] || []),
            tableColumns: groupFieldsByHeader(data?.[1] || []),
            yardCheckInfo: mapYardCheckInfo(data[0] || null),
        };
    }, [data]);

    const handleFilterChange = useCallback((newCriteria) => {
        setFilterCriteria((prev) => ({ ...prev, ...newCriteria }));
    }, []);

    const filteredDatasource = useMemo(() => {
        if (!displayedTableData?.length) return [];
        const searchText = filterCriteria.searchText.toLowerCase();
        return displayedTableData.filter((item) => {
            const matchesSearchText = deepSearch(item, searchText);
            const matchesStatus = filterCriteria.statuses[item.chipStatus];
            return matchesSearchText && matchesStatus;
        });
    }, [displayedTableData, filterCriteria]);

    const mapToPayload = useCallback((items) => {
        return items?.map(({ id, inventorySourceDataId, action, autoId, initialInventoryPresence }) => {
            const shouldIncludeSourceDataId = !(
                [SUMMARY_ACTION.UPDATE, SUMMARY_ACTION.ADD].includes(action) ||
                (action === SUMMARY_ACTION.SKIP && initialInventoryPresence !== MISSING)
            );

            return {
                inventorySourceDataId: shouldIncludeSourceDataId ? inventorySourceDataId : undefined,
                yardCheckItemId: id,
                action,
                autoId,
            };
        });
    }, []);

    useEffect(() => {
        setDisplayedTableData(tableData);
    }, [tableData]);

    useEffect(() => {
        setSummaryTableData(filteredDatasource);
        setPayload({ yardCheckItems: mapToPayload(displayedTableData), locationId: selectedLocationId });
    }, [displayedTableData, filteredDatasource, mapToPayload, selectedLocationId]);

    const handleActionChange = useCallback((updatedItem) => {
        const { autoId, action } = updatedItem;

        setDisplayedTableData((prev) => prev.map((item) => (item.autoId === autoId ? { ...item, action } : item)));

        setPayload((prev) => ({
            ...prev,
            yardCheckItems: prev.yardCheckItems.map((item) =>
                item.autoId === autoId
                    ? {
                          ...item,
                          action,
                          inventorySourceDataId:
                              [SUMMARY_ACTION.UPDATE, SUMMARY_ACTION.ADD].includes(action) ||
                              (action === SUMMARY_ACTION.SKIP && updatedItem.initialInventoryPresence !== MISSING)
                                  ? undefined
                                  : updatedItem.inventorySourceDataId,
                          yardCheckItemId: updatedItem.id,
                      }
                    : item
            ),
        }));
    }, []);

    const handleSubmitYardCheckSummary = useCallback(
        (payload) => {
            if (summaryId) {
                return onSubmitYardCheckSummary(summaryId, payload);
            }
        },
        [onSubmitYardCheckSummary, summaryId]
    );

    return {
        tableData,
        tableColumns,
        isLoading,
        isFetching,
        handleRefresh,
        fetchYardCheckSummary,
        handleSubmitYardCheckSummary,
        yardCheckInfo,
        isSubmitYardCheckLoading,
        filterCriteria,
        filteredDatasource,
        summaryTableData,
        handleFilterChange,
        handleActionChange,
        payload,
    };
};

export default useYardCheckSummary;
